import React from "react";
import api, { postForm } from "../api/client";
import { useOrg } from "../context/OrgContext";
import { useAuth } from "../context/AuthContext";
import { formatDateTime } from "../utils/datetime";
import { isStrongPassword, PASSWORD_POLICY_MESSAGE } from "../utils/password";
import {
  ModalShell,
  Pager,
  PasswordCriteriaInfo,
  usePagination,
} from "./ui-helpers";
import PageHeader from "./PageHeader";
import PinModal, { usePinGate } from "./PinModal";

const SUPERADMIN_ADDABLE = ["Viewer", "User", "Manager", "Admin"];
const ADMIN_ADDABLE = ["Viewer", "User", "Manager", "Admin"];
const ORG_CRITERIA = [
  { value: "<", label: "Below threshold" },
  { value: "=", label: "Equals threshold" },
  { value: ">", label: "Above threshold" },
];

function slugifyName(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function normalizeUserRows(rows = []) {
  const grouped = new Map();
  rows
    .filter((row) => String(row.role || "").toLowerCase() !== "vendor")
    .forEach((row) => {
      const key = row.uid;
      if (!grouped.has(key)) {
        grouped.set(key, {
          uid: row.uid,
          email: row.email || "",
          suspended: !!row.suspended,
          must_change: !!row.must_change,
          created_at: row.created_at,
          email_verified: Number(row.email_verified ?? 1),
          email_verified_at: row.email_verified_at || "",
          roles: [],
        });
      }
      const current = grouped.get(key);
      current.roles = Array.from(new Set([...(current.roles || []), row.role]));
      current.email = current.email || row.email || "";
      current.created_at = current.created_at || row.created_at;
      current.suspended = !!row.suspended;
      current.must_change = !!row.must_change;
      if (row.email_verified !== undefined && row.email_verified !== null)
        current.email_verified = Number(row.email_verified);
      current.email_verified_at =
        current.email_verified_at || row.email_verified_at || "";
    });
  return Array.from(grouped.values()).sort((a, b) =>
    a.uid.localeCompare(b.uid),
  );
}

export default function TeamsManagementWorkspace({
  title,
  subtitle,
  eyebrow,
  icon = "users",
  showOrgTab = false,
}) {
  const { current: org, refreshOrgs, setOrg } = useOrg();
  const { user } = useAuth();
  const pinGate = usePinGate();
  const addableRoles = showOrgTab ? SUPERADMIN_ADDABLE : ADMIN_ADDABLE;
  const isProtectedAdminSelf = (uid) =>
    !showOrgTab &&
    user?.currentRole === "Admin" &&
    String(uid || "").trim().toLowerCase() ===
      String(user?.uid || "").trim().toLowerCase();
  const [activeTab, setActiveTab] = React.useState(
    showOrgTab ? "add-org" : "add-user",
  );
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");

  const [orgs, setOrgs] = React.useState([]);
  const [orgSearch, setOrgSearch] = React.useState("");
  const [newOrg, setNewOrg] = React.useState({
    name: "",
    checklist_enabled: true,
    local_vendor_enabled: false,
    model_choice: "LLM",
    crit_op: "<",
    crit_threshold: 70,
    quota_mode: "both",
    monthly_limit: 1000,
    yearly_limit: 12000,
    yellow_threshold_pct: 70,
    red_threshold_pct: 90,
  });
  const [editOrg, setEditOrg] = React.useState(null);

  const [newUser, setNewUser] = React.useState({
    uid: "",
    password: "",
    email: "",
    role: addableRoles[0] || "User",
  });
  const [userRows, setUserRows] = React.useState([]);
  const [userSearch, setUserSearch] = React.useState("");
  const [roleFilter, setRoleFilter] = React.useState("");
  const [editUser, setEditUser] = React.useState(null);
  const [deleteUserTarget, setDeleteUserTarget] = React.useState(null);

  const showFeedback = (nextMessage = "", nextError = "") => {
    setMessage(nextMessage);
    setError(nextError);
  };

  const load = React.useCallback(async () => {
    const requests = [api.get("/admin/users/list")];
    if (showOrgTab) requests.unshift(api.get("/superadmin/orgs/list"));
    const results = await Promise.allSettled(requests);

    if (showOrgTab) {
      const orgRes = results[0];
      if (orgRes.status === "fulfilled") setOrgs(orgRes.value.data?.orgs || []);
      else setOrgs([]);
    }

    const userRes = results[showOrgTab ? 1 : 0];
    if (userRes.status === "fulfilled")
      setUserRows(normalizeUserRows(userRes.value.data?.users || []));
    else setUserRows([]);
  }, [showOrgTab]);

  React.useEffect(() => {
    load().catch(() => showFeedback("", "Unable to load management data."));
  }, [load, org]);

  const visibleOrgs = React.useMemo(() => {
    const needle = orgSearch.trim().toLowerCase();
    return (orgs || [])
      .filter(
        (item) =>
          !needle ||
          `${item.name} ${item.slug} ${item.model_choice}`
            .toLowerCase()
            .includes(needle),
      )
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
  }, [orgs, orgSearch]);

  const visibleUsers = React.useMemo(() => {
    const needle = userSearch.trim().toLowerCase();
    return (userRows || [])
      .filter((item) => {
        if (roleFilter && !(item.roles || []).includes(roleFilter))
          return false;
        if (!needle) return true;
        return `${item.uid} ${item.email} ${(item.roles || []).join(" ")}`
          .toLowerCase()
          .includes(needle);
      })
      .sort((a, b) => a.uid.localeCompare(b.uid));
  }, [userRows, userSearch, roleFilter]);

  const orgPager = usePagination(visibleOrgs, 10);
  const userPager = usePagination(visibleUsers, 10);

  const submitOrg = async () => {
    showFeedback();
    const name = (newOrg.name || "").trim();
    if (!name) {
      showFeedback("", "Organization name is required.");
      return;
    }
    const crit = Number(newOrg.crit_threshold);
    if (!Number.isFinite(crit) || crit < 0) {
      showFeedback("", "Critical value must be 0 or greater.");
      return;
    }
    try {
      await postForm("/superadmin/orgs/add", {
        name,
        checklist_enabled: newOrg.checklist_enabled ? "1" : "0",
        local_vendor_enabled: newOrg.local_vendor_enabled ? "1" : "0",
        model_choice: newOrg.model_choice,
        crit_op: newOrg.crit_op,
        crit_threshold: String(crit),
        quota_mode: newOrg.quota_mode || "both",
        monthly_limit: String(newOrg.monthly_limit ?? 1000),
        yearly_limit: String(newOrg.yearly_limit ?? 12000),
        yellow_threshold_pct: String(newOrg.yellow_threshold_pct ?? 70),
        red_threshold_pct: String(newOrg.red_threshold_pct ?? 90),
      });
      setNewOrg((current) => ({
        ...current,
        name: "",
        crit_threshold: 70,
        quota_mode: "both",
        monthly_limit: 1000,
        yearly_limit: 12000,
        yellow_threshold_pct: 70,
        red_threshold_pct: 90,
      }));
      showFeedback("Organization added.", "");
      await refreshOrgs();
      await load();
    } catch (err) {
      showFeedback(
        "",
        err?.response?.data?.error || "Unable to add organization.",
      );
    }
  };

  const saveOrgEdit = async () => {
    if (!editOrg) return;
    showFeedback();
    const nextName = (editOrg.name || "").trim();
    const crit = Number(editOrg.crit_threshold);
    if (!nextName) {
      showFeedback("", "Organization name is required.");
      return;
    }
    if (!Number.isFinite(crit) || crit < 0) {
      showFeedback("", "Critical value must be 0 or greater.");
      return;
    }
    try {
      const original = orgs.find(
        (item) => item.id === editOrg.id || item.slug === editOrg.slug,
      );
      if (original && nextName !== original.name) {
        await postForm("/superadmin/orgs/update", {
          org_id: editOrg.id,
          new_name: nextName,
        });
      }
      await postForm("/orgs/settings/update", {
        target_org: editOrg.id,
        checklist_enabled: editOrg.checklist_enabled ? "1" : "0",
        local_vendor_enabled: editOrg.local_vendor_enabled ? "1" : "0",
        model_choice: editOrg.model_choice,
        crit_op: editOrg.crit_op,
        crit_threshold: String(crit),
      });
      await postForm("/superadmin/orgs/suspend", {
        org_id: editOrg.id,
        suspended: editOrg.suspended ? "1" : "0",
      });
      setEditOrg(null);
      await refreshOrgs();
      await load();
      if (original && org === original.slug)
        setOrg(slugifyName(nextName) || editOrg.slug);
      showFeedback("Organization updated.", "");
    } catch (err) {
      showFeedback(
        "",
        err?.response?.data?.error || "Unable to update organization.",
      );
    }
  };

  const deleteOrg = async (item) => {
    if (
      !window.confirm(
        `Delete organization “${item.name}”? This cannot be undone.`,
      )
    )
      return;
    showFeedback();
    try {
      await postForm("/superadmin/orgs/delete", { org_id: item.id });
      await refreshOrgs();
      await load();
      showFeedback("Organization deleted.", "");
    } catch (err) {
      showFeedback(
        "",
        err?.response?.data?.error || "Unable to delete organization.",
      );
    }
  };

  const performSubmitUser = async (payload) => {
    try {
      await postForm("/admin/users/add", payload);
      setNewUser({
        uid: "",
        password: "",
        email: "",
        role: addableRoles[0] || "User",
      });
      await load();
      showFeedback("User added.", "");
    } catch (err) {
      showFeedback("", err?.response?.data?.error || "Unable to add user.");
    }
  };

  const submitUser = () => {
    showFeedback();
    if (!newUser.uid || !newUser.password || !newUser.role) {
      showFeedback("", "User ID, password, and role are required.");
      return;
    }
    if (!isStrongPassword(newUser.password)) {
      showFeedback("", PASSWORD_POLICY_MESSAGE);
      return;
    }
    const payload = {
      uid: newUser.uid,
      password: newUser.password,
      email: newUser.email,
      role: newUser.role,
    };
    pinGate.askPinThen(() => performSubmitUser(payload));
  };

  const performSaveUser = async (payload) => {
    try {
      await postForm("/admin/users/update", payload.update);
      if (payload.suspend) {
        await postForm("/admin/users/suspend", payload.suspend);
      }
      setEditUser(null);
      await load();
      showFeedback("User updated.", "");
    } catch (err) {
      showFeedback("", err?.response?.data?.error || "Unable to update user.");
    }
  };

  const saveUser = () => {
    if (!editUser) return;
    showFeedback();
    if (!editUser.uid) {
      showFeedback("", "User ID is required.");
      return;
    }
    if (editUser.password && !isStrongPassword(editUser.password)) {
      showFeedback("", PASSWORD_POLICY_MESSAGE);
      return;
    }
    const protectedSelf = isProtectedAdminSelf(editUser.originalUid);
    const selectedRoles = Array.from(new Set(editUser.roles || []));
    if (protectedSelf && !selectedRoles.includes("Admin")) {
      showFeedback("", "You cannot remove your own Admin role.");
      return;
    }
    if (protectedSelf && editUser.suspended) {
      showFeedback("", "You cannot suspend your own Admin account.");
      return;
    }
    const original = userRows.find(
      (item) => item.uid === editUser.originalUid,
    ) || { suspended: false };
    const payload = {
      update: {
        uid: editUser.originalUid,
        role: "User",
        new_uid: editUser.uid,
        new_email: editUser.email,
        new_password: editUser.password || undefined,
        new_roles: JSON.stringify(selectedRoles),
      },
      suspend: !!original.suspended !== !!editUser.suspended
        ? {
            uid: editUser.uid || editUser.originalUid,
            role: "User",
            suspended: editUser.suspended ? "1" : "0",
          }
        : null,
    };
    pinGate.askPinThen(() => performSaveUser(payload));
  };

  const requestDeleteUser = (row) => {
    if (isProtectedAdminSelf(row?.uid)) {
      showFeedback("", "You cannot delete your own Admin account.");
      return;
    }
    setDeleteUserTarget(row);
    showFeedback();
  };

  const performDeleteUser = async (row) => {
    try {
      for (const role of row.roles || []) {
        await postForm("/admin/users/delete", { uid: row.uid, role });
      }
      setDeleteUserTarget(null);
      await load();
      showFeedback("User deleted.", "");
    } catch (err) {
      showFeedback("", err?.response?.data?.error || "Unable to delete user.");
    }
  };

  const confirmDeleteUser = () => {
    const row = deleteUserTarget;
    if (!row) return;
    showFeedback();
    pinGate.askPinThen(() => performDeleteUser(row));
  };

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow={eyebrow}
        icon={icon}
        title={title}
        subtitle={subtitle}
      />

      <div className="tabs teams-management-tabs">
        {showOrgTab ? (
          <>
            <button
              className={activeTab === "add-org" ? "chip chip-on" : "chip"}
              type="button"
              onClick={() => setActiveTab("add-org")}
            >
              Add Organization
            </button>
            <button
              className={activeTab === "manage-org" ? "chip chip-on" : "chip"}
              type="button"
              onClick={() => setActiveTab("manage-org")}
            >
              Manage Organizations
            </button>
          </>
        ) : null}
        <button
          className={activeTab === "add-user" ? "chip chip-on" : "chip"}
          type="button"
          onClick={() => setActiveTab("add-user")}
        >
          Add Users
        </button>
        <button
          className={activeTab === "manage-user" ? "chip chip-on" : "chip"}
          type="button"
          onClick={() => setActiveTab("manage-user")}
        >
          Manage Users
        </button>
      </div>

      {message ? <div className="banner success">{message}</div> : null}
      {error ? <div className="banner error">{error}</div> : null}

      {showOrgTab && activeTab === "add-org" ? (
        <div className="section teams-form-card teams-tab-panel">
          <div className="page-header-row">
            <div>
              <h3>Add organization</h3>
              <div className="helper-text">
                Keep organization setup compact and deliberate while preserving
                the current creation flow.
              </div>
            </div>
          </div>
          <div className="form-grid teams-org-form teams-org-form--add">
            <div className="field">
              <label>
                New organization
                <input
                  value={newOrg.name}
                  onChange={(e) =>
                    setNewOrg((current) => ({
                      ...current,
                      name: e.target.value,
                    }))
                  }
                />
              </label>
            </div>
            <div className="field">
              <label>
                Critical criteria
                <select
                  value={newOrg.crit_op}
                  onChange={(e) =>
                    setNewOrg((current) => ({
                      ...current,
                      crit_op: e.target.value,
                    }))
                  }
                >
                  {ORG_CRITERIA.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="field">
              <label>
                Critical value
                <input
                  type="number"
                  min="0"
                  value={newOrg.crit_threshold}
                  onChange={(e) =>
                    setNewOrg((current) => ({
                      ...current,
                      crit_threshold: e.target.value,
                    }))
                  }
                />
              </label>
            </div>
            <div className="field">
              <label>
                Select extraction engine
                <select
                  value={newOrg.model_choice}
                  onChange={(e) =>
                    setNewOrg((current) => ({
                      ...current,
                      model_choice: e.target.value,
                    }))
                  }
                >
                  <option value="LLM">LLM</option>
                  <option value="ML">ML</option>
                </select>
              </label>
            </div>
            <label className="field checkbox-row checkbox-tile">
              <input
                type="checkbox"
                checked={!!newOrg.checklist_enabled}
                onChange={(e) =>
                  setNewOrg((current) => ({
                    ...current,
                    checklist_enabled: e.target.checked,
                  }))
                }
              />
              <span>Checklists enabled</span>
            </label>
            <label className="field checkbox-row checkbox-tile">
              <input
                type="checkbox"
                checked={!!newOrg.local_vendor_enabled}
                onChange={(e) =>
                  setNewOrg((current) => ({
                    ...current,
                    local_vendor_enabled: e.target.checked,
                  }))
                }
              />
              <span>Local vendor enabled</span>
            </label>
            <div className="field">
              <label>
                Quota mode
                <select
                  value={newOrg.quota_mode}
                  onChange={(e) =>
                    setNewOrg((current) => ({
                      ...current,
                      quota_mode: e.target.value,
                    }))
                  }
                >
                  <option value="monthly">Monthly only</option>
                  <option value="yearly">Yearly only</option>
                  <option value="both">Monthly + Yearly</option>
                </select>
              </label>
            </div>
            <div className="field">
              <label>
                Monthly limit
                <input
                  type="number"
                  min="0"
                  value={newOrg.monthly_limit}
                  onChange={(e) =>
                    setNewOrg((current) => ({
                      ...current,
                      monthly_limit: e.target.value,
                    }))
                  }
                  disabled={newOrg.quota_mode === "yearly"}
                />
              </label>
            </div>
            <div className="field">
              <label>
                Yearly limit
                <input
                  type="number"
                  min="0"
                  value={newOrg.yearly_limit}
                  onChange={(e) =>
                    setNewOrg((current) => ({
                      ...current,
                      yearly_limit: e.target.value,
                    }))
                  }
                  disabled={newOrg.quota_mode === "monthly"}
                />
              </label>
            </div>
            <div className="field">
              <label>
                Yellow threshold (%)
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={newOrg.yellow_threshold_pct}
                  onChange={(e) =>
                    setNewOrg((current) => ({
                      ...current,
                      yellow_threshold_pct: e.target.value,
                    }))
                  }
                />
              </label>
            </div>
            <div className="field">
              <label>
                Red threshold (%)
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={newOrg.red_threshold_pct}
                  onChange={(e) =>
                    setNewOrg((current) => ({
                      ...current,
                      red_threshold_pct: e.target.value,
                    }))
                  }
                />
              </label>
            </div>
          </div>
          <div className="card-actions">
            <button
              className="btn btn-primary"
              type="button"
              onClick={submitOrg}
            >
              Add organization
            </button>
          </div>
        </div>
      ) : null}

      {showOrgTab && activeTab === "manage-org" ? (
        <div className="section teams-tab-panel">
          <div className="filters-bar teams-table-toolbar">
            <label className="field-grow">
              <span>Search organizations</span>
              <input
                value={orgSearch}
                onChange={(e) => setOrgSearch(e.target.value)}
                placeholder="Search name, slug, or engine"
              />
            </label>
          </div>
          <div className="table--card">
            <div className="table-scroll">
              <table className="table table--dense table--fixed teams-table teams-table--orgs">
                <thead>
                  <tr>
                    <th>Organization</th>
                    <th>Slug</th>
                    <th>Engine</th>
                    <th>Checklist</th>
                    <th>Local vendor</th>
                    <th>Critical rule</th>
                    <th>Usage</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orgPager.pageData.map((item) => {
                    const q = item.quota || {};
                    const mode = String(q.quota_mode || "both").toLowerCase();
                    const showM = mode === "monthly" || mode === "both";
                    const showY = mode === "yearly" || mode === "both";
                    const m = q.monthly?.percent_used;
                    const y = q.yearly?.percent_used;
                    const usageBits = [];
                    if (showM && m !== undefined)
                      usageBits.push(`M ${Number(m).toFixed(0)}%`);
                    if (showY && y !== undefined)
                      usageBits.push(`Y ${Number(y).toFixed(0)}%`);
                    if (q.held_count) usageBits.push(`${q.held_count} on hold`);
                    return (
                      <tr key={item.id}>
                        <td>
                          <span className="status-primary-text">
                            {item.name}
                          </span>
                        </td>
                        <td>{item.slug}</td>
                        <td>{item.model_choice || "LLM"}</td>
                        <td>
                          {item.checklist_enabled ? "Enabled" : "Disabled"}
                        </td>
                        <td>
                          {item.local_vendor_enabled ? "Enabled" : "Disabled"}
                        </td>
                        <td>
                          {item.crit_op || "<"} {item.crit_threshold ?? 70}
                        </td>
                        <td>
                          {usageBits.length ? usageBits.join(" • ") : "—"}
                        </td>
                        <td>
                          <span
                            className={
                              item.suspended
                                ? "status-pill status-pending"
                                : "status-pill status-approved"
                            }
                          >
                            {item.suspended ? "Suspended" : "Active"}
                          </span>
                        </td>
                        <td>{formatDateTime(item.created_at)}</td>
                        <td>
                          <div className="table-actions">
                            <button
                              className="btn btn-secondary"
                              type="button"
                              onClick={() => setEditOrg({ ...item })}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-danger"
                              type="button"
                              onClick={() => deleteOrg(item)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {visibleOrgs.length === 0 ? (
                    <tr>
                      <td colSpan={10}>No organizations found.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <Pager {...orgPager} />
          </div>
        </div>
      ) : null}

      {activeTab === "add-user" ? (
        <>
          <div className="section teams-form-card">
            <div className="helper-text" style={{ marginBottom: 12 }}>
              {showOrgTab ? (
                <>
                  Managing users for organization{" "}
                  <strong>{org || "default"}</strong>.
                </>
              ) : (
                "Managing users for this workspace."
              )}
            </div>
            <div className="page-header-row">
              <div>
                <h3>Add user</h3>
                <div className="helper-text">Add New Users.</div>
              </div>
            </div>
            <div className="form-grid teams-user-form">
              <div className="field span-4">
                <label>
                  User ID
                  <input
                    value={newUser.uid}
                    onChange={(e) =>
                      setNewUser((current) => ({
                        ...current,
                        uid: e.target.value,
                      }))
                    }
                  />
                </label>
              </div>
              <div className="field span-4">
                <label>
                  Email
                  <input
                    value={newUser.email}
                    onChange={(e) =>
                      setNewUser((current) => ({
                        ...current,
                        email: e.target.value,
                      }))
                    }
                  />
                </label>
              </div>
              <div className="field span-4">
                <label>
                  Role
                  <select
                    value={newUser.role}
                    onChange={(e) =>
                      setNewUser((current) => ({
                        ...current,
                        role: e.target.value,
                      }))
                    }
                  >
                    {addableRoles.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="field span-12 password-field">
                <label>
                  <span className="field-label-inline">
                    <span>Password</span>
                    <PasswordCriteriaInfo />
                  </span>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) =>
                      setNewUser((current) => ({
                        ...current,
                        password: e.target.value,
                      }))
                    }
                  />
                </label>
              </div>
            </div>
            <div className="card-actions">
              <button
                className="btn btn-primary"
                type="button"
                onClick={submitUser}
              >
                Add user
              </button>
            </div>
          </div>
        </>
      ) : null}

      {activeTab === "manage-user" ? (
        <>
          <div className="section">
            <div className="filters-bar teams-table-toolbar">
              <label className="field-grow">
                <span>Search users</span>
                <input
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search user ID, email, or role"
                />
              </label>
              <label>
                <span>Filter role</span>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <option value="">All roles</option>
                  {addableRoles.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="table--card">
              <div className="table-scroll">
                <table className="table table--dense table--fixed teams-table teams-table--users">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Roles</th>
                      <th>Status</th>
                      <th>Password reset</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userPager.pageData.map((item) => (
                      <tr key={item.uid}>
                        <td>
                          <span className="status-primary-text">
                            {item.uid}
                          </span>
                        </td>
                        <td>
                          {item.email || "—"}
                          {Number(item.email_verified ?? 1) === 0 ? (
                            <span className="status-pill status-pending ml-2">
                              Unverified
                            </span>
                          ) : (
                            <span className="status-pill status-approved ml-2">
                              Verified
                            </span>
                          )}
                        </td>
                        <td>{(item.roles || []).join(", ")}</td>
                        <td>
                          <span
                            className={
                              item.suspended
                                ? "status-pill status-pending"
                                : "status-pill status-approved"
                            }
                          >
                            {item.suspended ? "Suspended" : "Active"}
                          </span>
                        </td>
                        <td>{item.must_change ? "Required" : "No"}</td>
                        <td>{formatDateTime(item.created_at)}</td>
                        <td>
                          <div className="table-actions">
                            <button
                              className="btn btn-secondary"
                              type="button"
                              onClick={() =>
                                setEditUser({
                                  originalUid: item.uid,
                                  uid: item.uid,
                                  email: item.email || "",
                                  password: "",
                                  roles: [...(item.roles || [])],
                                  suspended: !!item.suspended,
                                  email_verified: Number(
                                    item.email_verified ?? 1,
                                  ),
                                })
                              }
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-danger"
                              type="button"
                              disabled={isProtectedAdminSelf(item.uid)}
                              title={
                                isProtectedAdminSelf(item.uid)
                                  ? "You cannot delete your own Admin account."
                                  : "Delete user"
                              }
                              onClick={() => requestDeleteUser(item)}
                            >
                              Delete
                            </button>
                            {isProtectedAdminSelf(item.uid) ? (
                              <span className="helper-text">
                                Your Admin access is self-protected.
                              </span>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {visibleUsers.length === 0 ? (
                      <tr>
                        <td colSpan={7}>No users found for this workspace.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              <Pager {...userPager} />
            </div>
          </div>
        </>
      ) : null}

      <ModalShell
        open={!!editOrg}
        onClose={() => setEditOrg(null)}
        title="Edit organization"
        subtitle="Rename the org, adjust extraction settings, or suspend access."
        size="lg"
        className="teams-modal"
        actions={
          <>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => setEditOrg(null)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={saveOrgEdit}
            >
              Save changes
            </button>
          </>
        }
      >
        {editOrg ? (
          <div className="form-grid teams-org-form">
            <div className="field span-6">
              <label>
                Organization name
                <input
                  value={editOrg.name}
                  onChange={(e) =>
                    setEditOrg((current) => ({
                      ...current,
                      name: e.target.value,
                    }))
                  }
                />
              </label>
            </div>
            <div className="field span-6">
              <label>
                Slug
                <input value={editOrg.slug || ""} disabled />
              </label>
            </div>
            <div className="field span-4">
              <label>
                Extraction engine
                <select
                  value={editOrg.model_choice || "LLM"}
                  onChange={(e) =>
                    setEditOrg((current) => ({
                      ...current,
                      model_choice: e.target.value,
                    }))
                  }
                >
                  <option value="LLM">LLM</option>
                  <option value="ML">ML</option>
                </select>
              </label>
            </div>
            <div className="field span-4">
              <label>
                Critical criteria
                <select
                  value={editOrg.crit_op || "<"}
                  onChange={(e) =>
                    setEditOrg((current) => ({
                      ...current,
                      crit_op: e.target.value,
                    }))
                  }
                >
                  {ORG_CRITERIA.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="field span-4">
              <label>
                Critical value
                <input
                  type="number"
                  min="0"
                  value={editOrg.crit_threshold ?? 70}
                  onChange={(e) =>
                    setEditOrg((current) => ({
                      ...current,
                      crit_threshold: e.target.value,
                    }))
                  }
                />
              </label>
            </div>
            <div className="field span-12 teams-toggle-row">
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={!!editOrg.checklist_enabled}
                  onChange={(e) =>
                    setEditOrg((current) => ({
                      ...current,
                      checklist_enabled: e.target.checked,
                    }))
                  }
                />
                <span>Checklist enabled</span>
              </label>
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={!!editOrg.local_vendor_enabled}
                  onChange={(e) =>
                    setEditOrg((current) => ({
                      ...current,
                      local_vendor_enabled: e.target.checked,
                    }))
                  }
                />
                <span>Local vendor</span>
              </label>
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={!!editOrg.suspended}
                  onChange={(e) =>
                    setEditOrg((current) => ({
                      ...current,
                      suspended: e.target.checked,
                    }))
                  }
                />
                <span>Suspend organization</span>
              </label>
            </div>
            <div className="field span-12 helper-text">
              Detailed quota configuration (limits, thresholds, manual
              adjustments) is available in the Usage Ledger workspace.
            </div>
          </div>
        ) : null}
      </ModalShell>

      <ModalShell
        open={!!deleteUserTarget}
        onClose={() => setDeleteUserTarget(null)}
        title="Delete user"
        subtitle="Confirm before removing this user’s workspace access."
        className="teams-modal"
        actions={
          <>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => setDeleteUserTarget(null)}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger"
              type="button"
              onClick={confirmDeleteUser}
            >
              Delete
            </button>
          </>
        }
      >
        {deleteUserTarget ? (
          <div className="modal-copy-stack">
            <p>
              Delete <strong>{deleteUserTarget.uid}</strong> from this
              workspace?
            </p>
            <div className="helper-text">
              Role{(deleteUserTarget.roles || []).length === 1 ? "" : "s"}:{" "}
              {(deleteUserTarget.roles || []).join(", ") || "—"}
            </div>
          </div>
        ) : null}
      </ModalShell>

      <ModalShell
        open={!!editUser}
        onClose={() => setEditUser(null)}
        title="Edit user"
        subtitle="Update identity, roles, password, or suspension status."
        className="teams-modal"
        actions={
          <>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => setEditUser(null)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={saveUser}
            >
              Save changes
            </button>
          </>
        }
      >
        {editUser ? (
          <div className="form-grid teams-user-form">
            <div className="field span-6">
              <label>
                User ID
                <input
                  value={editUser.uid}
                  onChange={(e) =>
                    setEditUser((current) => ({
                      ...current,
                      uid: e.target.value,
                    }))
                  }
                />
              </label>
            </div>
            <div className="field span-6">
              <label>
                Email
                <input
                  value={editUser.email}
                  onChange={(e) =>
                    setEditUser((current) => ({
                      ...current,
                      email: e.target.value,
                    }))
                  }
                />
              </label>
              {Number(editUser.email_verified ?? 1) === 0 ? (
                <div className="helper-text">
                  This email is currently unverified. The user will verify it on
                  next login.
                </div>
              ) : null}
            </div>
            <div className="field span-12 password-field">
              <label>
                <span className="field-label-inline">
                  <span>Reset password</span>
                  <PasswordCriteriaInfo />
                </span>
                <input
                  type="password"
                  placeholder="Leave blank to keep current password"
                  value={editUser.password}
                  onChange={(e) =>
                    setEditUser((current) => ({
                      ...current,
                      password: e.target.value,
                    }))
                  }
                />
              </label>
            </div>
            <div className="field span-12">
              <label>Roles</label>
              <div className="checklist-grid">
                {addableRoles.map((role) => {
                  const lockedSelfAdmin =
                    role === "Admin" &&
                    isProtectedAdminSelf(editUser.originalUid);
                  return (
                    <label key={role} className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={(editUser.roles || []).includes(role)}
                        disabled={lockedSelfAdmin}
                        onChange={() => {
                          if (lockedSelfAdmin) return;
                          setEditUser((current) => ({
                            ...current,
                            roles: current.roles.includes(role)
                              ? current.roles.filter((item) => item !== role)
                              : [...current.roles, role],
                          }));
                        }}
                      />
                      <span>{role}</span>
                    </label>
                  );
                })}
              </div>
              {isProtectedAdminSelf(editUser.originalUid) ? (
                <div className="helper-text">
                  Your own Admin role must remain enabled.
                </div>
              ) : null}
            </div>
            <div className="field span-12 teams-toggle-row">
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={!!editUser.suspended}
                  disabled={isProtectedAdminSelf(editUser.originalUid)}
                  onChange={(e) => {
                    if (isProtectedAdminSelf(editUser.originalUid)) return;
                    setEditUser((current) => ({
                      ...current,
                      suspended: e.target.checked,
                    }));
                  }}
                />
                <span>
                  Suspend user
                  {isProtectedAdminSelf(editUser.originalUid)
                    ? " (unavailable for your own account)"
                    : ""}
                </span>
              </label>
            </div>
          </div>
        ) : null}
      </ModalShell>
      <PinModal
        uid={user?.uid || ""}
        open={pinGate.pinOpen}
        onClose={pinGate.closePin}
        onVerified={pinGate.handlePinVerified}
      />
    </div>
  );
}
